/**
 * Guideline, summary, and PDF track helpers for Medical Necessity analytics.
 * @module medicalNecessityAnalytics/guidelineTrackers
 */
import { NCCNEvaluationRecord } from "../../../data-model/nccnEvaluation";
import {
  EvaluationResult,
  HumanVerdict,
} from "../../../data-model/nccnGuidelines";
import { EventsName } from "../../../enums/eventsName";
import { bumpGuidelineReviewVersion } from "./guidelineReviewVersion";
import { buildLocationAttrs } from "./locationAttrs";
import {
  mapAiGuidelineVerdict,
  mapHumanGuidelineVerdict,
  mapOrderVerdict,
  CompletionMode,
  RhsStage,
} from "./mappers";
import {
  createAnalyticsId,
  MedicalNecessityAnalyticsSession,
} from "./sessionState";

/** Shared deps for guideline / PDF trackers. */
export interface GuidelineTrackerDeps {
  analysisId: string;
  session: MedicalNecessityAnalyticsSession;
  emit: (eventName: EventsName, additional: Record<string, unknown>) => void;
}

/**
 * Builds guideline completion, summary reopen, and PDF trackers.
 *
 * @param deps - Analysis ID, session, and emit
 * @returns Guideline and PDF track methods
 */
export const createGuidelineTrackers = ({
  analysisId,
  session,
  emit,
}: GuidelineTrackerDeps) => {
  const setRhsStage = (stage: RhsStage) => {
    session.setRhsStage(stage);
  };

  const getRhsStage = () => session.getRhsStage();

  const clearSummaryView = () => {
    session.clearSummaryView();
  };

  const trackVerdictNextGuidelineClicked = (
    guideline: NCCNEvaluationRecord,
    guidelineIndex: number,
  ): string => {
    const actionId = createAnalyticsId();
    const loc = buildLocationAttrs(analysisId, guideline, guidelineIndex);
    emit(EventsName.MEDICAL_NECESSITY_VERDICT_NEXT_GUIDELINE, {
      ...loc,
      action_id: actionId,
    });
    return actionId;
  };

  const trackSkipGuidelineClicked = (
    guideline: NCCNEvaluationRecord,
    guidelineIndex: number,
  ): string => {
    const actionId = createAnalyticsId();
    const loc = buildLocationAttrs(analysisId, guideline, guidelineIndex);
    emit(EventsName.MEDICAL_NECESSITY_SKIP_GUIDELINE_CLICKED, {
      ...loc,
      action_id: actionId,
    });
    return actionId;
  };

  const trackSkipCancelClicked = (
    guideline: NCCNEvaluationRecord,
    guidelineIndex: number,
  ): string => {
    const actionId = createAnalyticsId();
    const loc = buildLocationAttrs(analysisId, guideline, guidelineIndex);
    emit(EventsName.MEDICAL_NECESSITY_SKIP_CANCEL_CLICKED, {
      ...loc,
      action_id: actionId,
    });
    return actionId;
  };

  /**
   * Emits `skip_confirmed` when the reviewer confirms skipping the guideline.
   *
   * The skip reasoning text is never sent (PHI rule).
   *
   * @param guideline - Skipped guideline
   * @param guidelineIndex - One-based display index
   * @param actionId - Action id of the originating skip click
   */
  const trackSkipConfirmed = (
    guideline: NCCNEvaluationRecord,
    guidelineIndex: number,
    actionId?: string,
  ) => {
    const loc = buildLocationAttrs(analysisId, guideline, guidelineIndex);
    emit(EventsName.MEDICAL_NECESSITY_SKIP_CONFIRMED, {
      ...loc,
      ...(actionId ? { action_id: actionId } : {}),
    });
  };

  /**
   * Emits `guideline_completed` when a guideline review concludes — either the
   * reviewer saves the verdict (`finalized`) or confirms a skip (`skipped`).
   *
   * @param guideline - Concluded guideline
   * @param guidelineIndex - One-based display index
   * @param completionMode - How the guideline was concluded
   * @param humanVerdict - Human verdict the guideline concluded with (a skip is
   *   always Not Met; passed explicitly because the in-memory record may not
   *   yet reflect a just-persisted verdict)
   * @param actionId - Action id of the originating verdict save or skip
   */
  const trackGuidelineCompleted = (
    guideline: NCCNEvaluationRecord,
    guidelineIndex: number,
    completionMode: CompletionMode,
    humanVerdict: HumanVerdict | null | undefined,
    actionId?: string,
  ) => {
    const loc = buildLocationAttrs(analysisId, guideline, guidelineIndex);
    const aiVerdict = mapAiGuidelineVerdict(
      guideline.metadata?.evaluation?.final_result,
    );
    const items = guideline.metadata?.evaluation?.items ?? [];
    const criteriaCompletedCount = items.filter(
      (item) => !!item.ai_answer_feedback,
    ).length;
    const attrs: Record<string, unknown> = {
      ...loc,
      ...(actionId ? { action_id: actionId } : {}),
      ai_guideline_verdict: aiVerdict,
      human_guideline_verdict: mapHumanGuidelineVerdict(humanVerdict),
      completion_mode: completionMode,
      criteria_completed_count: criteriaCompletedCount,
    };
    emit(EventsName.MEDICAL_NECESSITY_GUIDELINE_COMPLETED, attrs);
  };

  const trackOverrideToMetClicked = (
    guideline: NCCNEvaluationRecord,
    guidelineIndex: number,
  ): string => {
    const actionId = createAnalyticsId();
    // Bump first: this click starts the new review pass, so location attrs and
    // the ensuing *_viewed events share that version (prior pass = version - 1).
    bumpGuidelineReviewVersion(analysisId, guideline.guideline_id ?? "");
    const loc = buildLocationAttrs(analysisId, guideline, guidelineIndex);
    emit(EventsName.MEDICAL_NECESSITY_OVERRIDE_TO_MET_CLICKED, {
      ...loc,
      action_id: actionId,
      human_guideline_verdict: mapHumanGuidelineVerdict(
        guideline.metadata?.human_review?.verdict,
      ),
    });
    return actionId;
  };

  const trackEditReviewClicked = (
    guideline: NCCNEvaluationRecord,
    guidelineIndex: number,
  ): string => {
    const actionId = createAnalyticsId();
    // Bump first: this click starts the new review pass, so location attrs and
    // the ensuing *_viewed events share that version (prior pass = version - 1).
    bumpGuidelineReviewVersion(analysisId, guideline.guideline_id ?? "");
    const loc = buildLocationAttrs(analysisId, guideline, guidelineIndex);
    emit(EventsName.MEDICAL_NECESSITY_GUIDELINE_EDIT_CLICKED, {
      ...loc,
      action_id: actionId,
      human_guideline_verdict: mapHumanGuidelineVerdict(
        guideline.metadata?.human_review?.verdict,
      ),
    });
    return actionId;
  };

  /**
   * Emits `verdict_review` when the reviewer clicks Review on the decision
   * screen (shown only once the human verdict is Met).
   *
   * @param guideline - Current guideline
   * @param guidelineIndex - One-based display index
   * @param humanVerdict - Human verdict selected on the decision screen
   * @param aiOrderVerdict - Order-level AI verdict
   * @param humanOrderVerdict - Order-level human verdict
   * @returns The action id correlating the ensuing summary view
   */
  const trackVerdictReviewClicked = (
    guideline: NCCNEvaluationRecord,
    guidelineIndex: number,
    humanVerdict: HumanVerdict | null | undefined,
    aiOrderVerdict: EvaluationResult | null | undefined,
    humanOrderVerdict: EvaluationResult | null | undefined,
  ): string => {
    const actionId = createAnalyticsId();
    const loc = buildLocationAttrs(analysisId, guideline, guidelineIndex);
    emit(EventsName.MEDICAL_NECESSITY_VERDICT_REVIEW, {
      ...loc,
      action_id: actionId,
      ai_order_verdict: mapOrderVerdict(aiOrderVerdict),
      human_order_verdict: mapOrderVerdict(humanOrderVerdict),
      human_guideline_verdict: mapHumanGuidelineVerdict(humanVerdict),
    });
    return actionId;
  };

  /**
   * Emits `guideline_copy_clicked` when the reviewer copies guideline text
   * (decision screen or an expanded summary row).
   *
   * @param guideline - Guideline whose text is copied
   * @param guidelineIndex - One-based display index
   */
  const trackGuidelineCopyClicked = (
    guideline: NCCNEvaluationRecord,
    guidelineIndex: number,
  ) => {
    const loc = buildLocationAttrs(analysisId, guideline, guidelineIndex);
    emit(EventsName.MEDICAL_NECESSITY_GUIDELINE_COPY_CLICKED, {
      ...loc,
      rhs_stage: session.getRhsStage(),
    });
  };

  /**
   * Emits `search_submitted` when the reviewer submits the document search.
   *
   * The query text itself is never sent (PHI rule); only the action id and
   * stage are recorded.
   *
   * @param _searchQuery - Search query (accepted for call-site compatibility;
   *   intentionally not emitted)
   * @returns The action id correlating the ensuing search results view
   */
  const trackSearchSubmitted = (_searchQuery: string) => {
    const actionId = createAnalyticsId();
    emit(EventsName.MEDICAL_NECESSITY_SEARCH_SUBMITTED, {
      action_id: actionId,
      rhs_stage: session.getRhsStage(),
      ...session.getLocationSnapshot(),
    });
    return actionId;
  };

  const trackDocContentsOpened = () => {
    emit(EventsName.MEDICAL_NECESSITY_DOC_CONTENTS_OPENED, {
      rhs_stage: session.getRhsStage(),
      ...session.getLocationSnapshot(),
    });
  };

  const trackPdfReportSelected = (reportId: string, reportTitle: string) => {
    const actionId = createAnalyticsId();
    emit(EventsName.MEDICAL_NECESSITY_PDF_REPORT_SELECTED, {
      action_id: actionId,
      rhs_stage: session.getRhsStage(),
      report_id: reportId,
      report_title: reportTitle,
      ...session.getLocationSnapshot(),
    });
    return actionId;
  };

  return {
    setRhsStage,
    getRhsStage,
    clearSummaryView,
    trackVerdictNextGuidelineClicked,
    trackSkipGuidelineClicked,
    trackSkipCancelClicked,
    trackSkipConfirmed,
    trackGuidelineCompleted,
    trackOverrideToMetClicked,
    trackEditReviewClicked,
    trackVerdictReviewClicked,
    trackGuidelineCopyClicked,
    trackSearchSubmitted,
    trackDocContentsOpened,
    trackPdfReportSelected,
  };
};
